import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, type SortingState } from "@tanstack/react-table";
import { toast } from "sonner";

import {
  fetchOrganizations,
  fetchOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  addUserToOrganization,
  removeUserFromOrganization,
  inviteUserByPhone,
  addUnitToOrganization,
  removeUnitFromOrganization,
  downloadOrganizationSessionsReport,
  fetchUsers,
  fetchUnits,
  Organization,
  OrganizationDetail,
  OrganizationUnitSummary,
  UserRecord,
} from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { SlidePanel } from "@/components/SlidePanel";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { PendingInvitations } from "@/components/PendingInvitations";
import { inviteSmsReasonText } from "@/lib/phone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DollarSign, Download, Search, Wifi, WifiOff, X, Zap } from "@/components/icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { statusBadgeProps } from "@/lib/unit-helpers";
import { useAuth } from "@/app/AuthContext";
import { OrgSessionsReport } from "@/views/OrgSessionsReport";
import { useLocalization, formatCalendarDate, FormatInputError, FORMAT_LOCALE } from "@voltie/localization-web";
import { localization, useT, type Translate } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

// Map a unit's status (online + state_name) to an icon + tailwind color +
// human label for the row in the org units list. Uses the same variant
// mapping as `statusBadgeProps` so colors stay in sync with the rest of the app.
//
// IMPORTANT: this project does NOT define text-success/text-warning/text-info
// tokens. The Badge component maps variants to real Tailwind colors internally
// (see components/ui/badge.tsx). Mirror that mapping here for icons.
function statusIconProps(
  t: Translate,
  isOnline: boolean | undefined,
  stateName: string | null | undefined,
): { Icon: React.ComponentType<{ className?: string }>; color: string; label: string } {
  if (isOnline === undefined) {
    return { Icon: Wifi, color: "text-muted-foreground/40", label: t("operator.orgs.unit_status_unknown") };
  }
  if (!isOnline) {
    return { Icon: WifiOff, color: "text-muted-foreground", label: t("common.status.offline") };
  }
  const s = (stateName || "").toLowerCase();
  // Charging gets its own icon — most useful at-a-glance signal.
  if (s === "charging" || s === "charging_simple") {
    return { Icon: Zap, color: "text-emerald-500", label: t("operator.orgs.unit_status_charging") };
  }
  const { variant } = statusBadgeProps(s, true);
  const colorByVariant: Record<typeof variant, string> = {
    success: "text-emerald-500",
    error: "text-red-600 dark:text-red-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-sky-600 dark:text-sky-400",
    primary: "text-primary",
    neutral: "text-muted-foreground",
  };
  return {
    Icon: Wifi,
    color: colorByVariant[variant],
    // A protocol state name is shown as sent (capitalized); it is not UI text.
    label: stateName ? stateName.charAt(0).toUpperCase() + stateName.slice(1) : t("common.status.online"),
  };
}

// paid_until is a calendar date ("YYYY-MM-DD"), never shifted by a zone; an unexpected
// shape is shown as sent. The expiry comparison below is unchanged.
function paidUntilLabel(paidUntil: string): string {
  try {
    return formatCalendarDate(paidUntil, "numeric");
  } catch (error) {
    if (error instanceof FormatInputError) return paidUntil;
    throw error;
  }
}

function paidBadge(paidUntil: string | null) {
  if (!paidUntil) return <span className="opacity-40">—</span>;
  const d = new Date(paidUntil + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const label = paidUntilLabel(paidUntil);
  return d >= now ? (
    <Badge variant="success">{label}</Badge>
  ) : (
    <Badge variant="error">{label}</Badge>
  );
}

/* ------------------------------------------------------------------ */
/*  Create / Edit Modal                                                */
/* ------------------------------------------------------------------ */

interface OrgFormData {
  name: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  paid_until: string;
  comment: string;
}

const emptyForm: OrgFormData = {
  name: "",
  contact_person: "",
  contact_phone: "",
  contact_email: "",
  paid_until: "",
  comment: "",
};

function OrgFormModal({
  open,
  onOpenChange,
  title,
  initial,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  initial: OrgFormData;
  onSubmit: (data: OrgFormData) => void;
  isPending: boolean;
}) {
  const t = useT();
  const [form, setForm] = useState<OrgFormData>(initial);

  React.useEffect(() => {
    if (open) setForm(initial);
  }, [open]);

  const set = (k: keyof OrgFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
        >
          <div>
            <Label>{t("operator.orgs.form_name")}</Label>
            <Input className="mt-1.5" value={form.name} onChange={set("name")} required minLength={2} maxLength={100} placeholder={t("operator.orgs.form_name_placeholder")} />
          </div>
          <div>
            <Label>{t("operator.orgs.form_contact_person")}</Label>
            <Input className="mt-1.5" value={form.contact_person} onChange={set("contact_person")} maxLength={200} placeholder={t("operator.orgs.form_contact_person_placeholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("operator.orgs.form_phone")}</Label>
              <Input className="mt-1.5" value={form.contact_phone} onChange={set("contact_phone")} maxLength={50} placeholder="+1 555-123-4567" />
            </div>
            <div>
              <Label>{t("operator.orgs.form_email")}</Label>
              <Input className="mt-1.5" type="email" value={form.contact_email} onChange={set("contact_email")} maxLength={200} placeholder="contact@example.com" />
            </div>
          </div>
          <div>
            <Label>{t("operator.orgs.form_paid_until")}</Label>
            <Input className="mt-1.5" type="date" value={form.paid_until} onChange={set("paid_until")} />
          </div>
          <div>
            <Label>{t("operator.orgs.form_comment")}</Label>
            <textarea
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              rows={3}
              maxLength={2000}
              value={form.comment}
              onChange={set("comment")}
              placeholder={t("operator.orgs.form_comment_placeholder")}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending || !form.name.trim()}>
              {isPending ? t("common.state.saving") : t("common.action.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Add User Modal                                                     */
/* ------------------------------------------------------------------ */

function AddUserModal({
  open,
  onOpenChange,
  orgId,
  existingUserIds,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: number;
  existingUserIds: Set<number>;
  onAdded: () => void;
}) {
  // Only a system admin may add an EXISTING account as a manager (the user-search
  // list below) or grant the 'manager' role. An org manager reaching this modal can
  // only invite DRIVERS by phone — the backend enforces both rules; the UI mirrors
  // them so a manager never sees controls that would 403.
  const { isAdmin } = useAuth();
  const t = useT();
  const [search, setSearch] = useState("");
  // Debounce so we don't fire on every keystroke.
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const usersQ = useQuery({
    queryKey: ["users", "addToOrg", searchDebounced],
    queryFn: () => fetchUsers({ q: searchDebounced || undefined, limit: 50 }),
    // The user directory (fetchUsers) is an admin-only endpoint; never query it for
    // a plain org manager (it would 403 and the list is hidden for them anyway).
    enabled: open && isAdmin,
  });

  const addM = useMutation({
    // This dialog adds organization MANAGERS. Drivers are created via the driver
    // import/membership path, not here. Role is passed explicitly (no default).
    mutationFn: (userId: number) => addUserToOrganization(orgId, userId, "manager"),
    onSuccess: () => {
      toast.success(t("operator.orgs.user_added"));
      onAdded();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.add_user_failed" })),
  });

  // Invite-by-phone: add someone who may not have an account yet. Existing users
  // are added immediately; unknown phones get a pending invitation that is
  // consumed when they register with that number.
  const [phone, setPhone] = useState("");
  const [inviteRole, setInviteRole] = useState<"manager" | "driver">("driver");
  const inviteQueryClient = useQueryClient();
  const inviteM = useMutation({
    mutationFn: () => inviteUserByPhone(orgId, phone.trim(), inviteRole),
    onSuccess: (res) => {
      if (res.status === "added") {
        // Usernames are joined like the original list ("a, b"); they are data, not words.
        const names = (res.added ?? []).map((a) => a.username).join(", ");
        toast.success(names ? t("operator.orgs.invite_added_existing", { names }) : t("operator.orgs.user_added"));
        onAdded();
        onOpenChange(false);
        return;
      }
      // Invites are idempotent per org+phone: a repeat re-uses the existing row
      // (and re-sends the text) rather than creating a second invitation.
      if (res.sms_sent) {
        toast.success(t("operator.orgs.invite_texted", { resend: res.already_pending ? "yes" : "no" }));
      } else {
        // The invitation IS recorded either way — say why the text didn't go out
        // rather than implying nothing happened. The reason text belongs to lib/phone.
        toast.warning(
          t("operator.orgs.invite_not_texted", { reason: inviteSmsReasonText(res.sms_reason, t) }),
        );
      }
      setPhone("");
      inviteQueryClient.invalidateQueries({ queryKey: ["org-invitations", orgId] });
      onAdded();
    },
    // The backend's stable error_code (invalid_phone / unsupported_country / role_conflict ...)
    // picks the message; its English sentence is not shown.
    onError: (e) => toast.error(errorMessage(t, e, { family: "org_invitation_create", fallback: "operator.orgs.invite_failed" })),
  });

  const available = useMemo(() => {
    const users = usersQ.data?.users ?? [];
    return users.filter((u) => !existingUserIds.has(u.id));
  }, [usersQ.data, existingUserIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("operator.orgs.add_user_title")}</DialogTitle>
        </DialogHeader>
        {/* Search + add an EXISTING account as a manager — system-admin only. */}
        {isAdmin && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
              <Input
                className="pl-9"
                placeholder={t("operator.orgs.search_users")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {usersQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("common.state.loading")}</div>}
              {available.length === 0 && !usersQ.isLoading && (
                <div className="text-sm opacity-50 py-4 text-center">{t("operator.orgs.no_matching_users")}</div>
              )}
              {available.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{u.username}</span>
                    <Badge variant={u.role === "admin" ? "primary" : "neutral"} className="badge-sm">
                      {t("operator.user_menu.system_role", { role: u.role })}
                    </Badge>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => addM.mutate(u.id)} disabled={addM.isPending}>
                    {t("common.action.add")}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Invite by phone — for people who don't have an account yet. */}
        <div className={`${isAdmin ? "border-t pt-3 mt-1 " : ""}space-y-2`}>
          <div className="text-sm font-medium">{t("operator.orgs.invite_title")}</div>
          <div className="text-xs opacity-60">
            {t("operator.orgs.invite_help")}
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="flex-1"
              type="tel"
              placeholder={t("operator.orgs.invite_phone_placeholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {/* Only a system admin may grant the 'manager' role. A plain org manager
                invites drivers only (server-enforced), so they see a fixed label. */}
            {isAdmin ? (
              <select
                className="h-9 rounded-4xl border border-input bg-input/30 px-3 text-sm"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as "manager" | "driver")}
              >
                <option value="driver">{t("operator.org_role.driver")}</option>
                <option value="manager">{t("operator.org_role.manager")}</option>
              </select>
            ) : (
              <span className="h-9 inline-flex items-center rounded-4xl border border-input bg-input/30 px-3 text-sm opacity-70">
                {t("operator.org_role.driver")}
              </span>
            )}
            <Button
              size="sm"
              onClick={() => inviteM.mutate()}
              disabled={inviteM.isPending || phone.trim().length < 5}
            >
              {inviteM.isPending ? "…" : t("operator.orgs.invite")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Unit Modal                                                     */
/* ------------------------------------------------------------------ */

function AddUnitModal({
  open,
  onOpenChange,
  orgId,
  existingUnitIds,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: number;
  existingUnitIds: Set<string>;
  onAdded: () => void;
}) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);
  const unitsQ = useQuery({
    queryKey: ["units-search", debouncedSearch],
    queryFn: () => fetchUnits({ start: 0, end: 50, search: debouncedSearch || undefined }),
    enabled: open,
  });

  const addM = useMutation({
    mutationFn: (unitId: string) => addUnitToOrganization(orgId, unitId),
    onSuccess: () => {
      toast.success(t("operator.orgs.unit_added"));
      onAdded();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.add_unit_failed" })),
  });

  const available = useMemo(() => {
    const units = unitsQ.data?.units ?? [];
    return units.filter((u) => !existingUnitIds.has(u.unit_id));
  }, [unitsQ.data, existingUnitIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("operator.orgs.add_unit_title")}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input
            className="pl-9"
            placeholder={t("operator.orgs.search_units")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("common.state.loading")}</div>}
          {available.length === 0 && !unitsQ.isLoading && (
            <div className="text-sm opacity-50 py-4 text-center">{t("operator.orgs.no_matching_units")}</div>
          )}
          {available.slice(0, 50).map((u) => (
            <div key={u.unit_id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm font-mono">{u.unit_id}</span>
                {u.name && <span className="text-xs opacity-50">{u.name}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.unit_id)} disabled={addM.isPending}>
                {t("common.action.add")}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Export Sessions CSV (admin only)                                   */
/* ------------------------------------------------------------------ */

function ExportSessionsSection({ orgId }: { orgId: number }) {
  const t = useT();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (start && end && end < start) {
      toast.error(t("operator.orgs.report.error_end_before_start"));
      return;
    }
    setDownloading(true);
    try {
      const { blob, filename } = await downloadOrganizationSessionsReport(orgId, {
        start: start || undefined,
        end: end || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("operator.orgs.report.downloaded"));
    } catch (e: any) {
      toast.error(errorMessage(t, e, { fallback: "operator.orgs.report.generate_failed" }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pt-4 border-t border-border">
      <h4 className="font-semibold text-sm mb-1">{t("operator.orgs.reports_title")}</h4>
      <p className="text-xs opacity-60 mb-2">{t("operator.orgs.full_export_help")}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">{t("operator.orgs.export_from")}</Label>
          <Input
            className="mt-1 h-8 w-[150px]"
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">{t("operator.orgs.export_to")}</Label>
          <Input
            className="mt-1 h-8 w-[150px]"
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
          <Download className="h-3.5 w-3.5 mr-1.5" />
          {downloading ? t("operator.orgs.report.generating") : t("operator.orgs.export_button")}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Organization Detail Panel                                          */
/* ------------------------------------------------------------------ */

export function OrgDetailPanel({
  orgId,
  onDeleted,
  mode = "panel",
}: {
  orgId: number;
  onDeleted: () => void;
  /** "panel" = brief slide-over (shows a link to the full page);
   *  "page"  = full details page (shows the Reports area). */
  mode?: "panel" | "page";
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const { isAdmin, managerOrgIds } = useAuth();
  // A manager of THIS org may invite people to it (drivers only — see AddUserModal);
  // a system admin may manage any org. managerOrgIds is empty for system admins.
  const canManageOrg = isAdmin || managerOrgIds.includes(orgId);
  const [editOpen, setEditOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUnitOpen, setAddUnitOpen] = useState(false);

  const detailQ = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => fetchOrganization(orgId),
    refetchInterval: 15_000,
  });

  const org = detailQ.data?.organization;

  const editM = useMutation({
    mutationFn: (data: OrgFormData) => updateOrganization(orgId, data),
    onSuccess: () => {
      toast.success(t("operator.orgs.updated"));
      setEditOpen(false);
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.update_failed" })),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteOrganization(orgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.deleted"));
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      onDeleted();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.delete_failed" })),
  });

  const removeUserM = useMutation({
    mutationFn: (userId: number) => removeUserFromOrganization(orgId, userId),
    onSuccess: () => {
      toast.success(t("operator.orgs.user_removed"));
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.remove_failed" })),
  });

  const removeUnitM = useMutation({
    mutationFn: (unitId: string) => removeUnitFromOrganization(orgId, unitId),
    onSuccess: () => {
      toast.success(t("operator.orgs.unit_removed"));
      detailQ.refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.remove_failed" })),
  });

  if (!org) {
    return <div className="text-sm opacity-50">{t("common.state.loading")}</div>;
  }

  const refreshDetail = () => {
    detailQ.refetch();
    queryClient.invalidateQueries({ queryKey: ["organizations"] });
  };

  return (
    <div className="space-y-5">
      {/* Meta */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">{org.name}</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>{t("common.action.edit")}</Button>
            {isAdmin && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (confirm(t("operator.orgs.delete_confirm", { name: org.name })))
                    deleteM.mutate();
                }}
              >
                {t("common.action.delete")}
              </Button>
            )}
          </div>
        </div>

        {mode === "panel" && (
          <Link
            to={`/organizations/${orgId}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            {t("operator.orgs.open_full_details")}
          </Link>
        )}

        <div className="text-xs opacity-60">
          {/* created_at display unchanged: its wire format is not proven aware (LOC-405 report). */}
          {t("operator.orgs.created_at", { time: new Date(org.created_at + "Z").toLocaleString() })}
        </div>

        {org.contact_person && (
          <div><span className="font-medium">{t("operator.orgs.detail_contact")}</span> {org.contact_person}</div>
        )}
        {org.contact_phone && (
          <div><span className="font-medium">{t("operator.orgs.detail_phone")}</span> {org.contact_phone}</div>
        )}
        {org.contact_email && (
          <div><span className="font-medium">{t("operator.orgs.detail_email")}</span> {org.contact_email}</div>
        )}
        {org.paid_until && (
          <div className="flex items-center gap-2">
            <span className="font-medium">{t("operator.orgs.detail_paid_until")}</span> {paidBadge(org.paid_until)}
          </div>
        )}
        {org.comment && (
          <div><span className="font-medium">{t("operator.orgs.detail_comment")}</span> {org.comment}</div>
        )}

        {org.account_custom_id && (
          <div className="mt-3 pt-3 border-t border-border space-y-1.5">
            <div>
              <span className="font-medium">{t("operator.orgs.detail_account_id")}</span>{" "}
              <span className="font-mono text-xs">{org.account_custom_id}</span>
            </div>
          </div>
        )}
      </div>

      {/* Users */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">{t("operator.orgs.users_heading", { count: org.users.length })}</h4>
          {canManageOrg && (
            <Button size="sm" variant="outline" onClick={() => setAddUserOpen(true)}>{t("operator.orgs.add_user_button")}</Button>
          )}
        </div>
        {org.users.length === 0 ? (
          <div className="text-sm opacity-50 italic">{t("operator.orgs.no_users")}</div>
        ) : (
          <div className="space-y-1">
            {org.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-sm">
                <div className="flex items-center gap-2">
                  {/* Admins can open the full User Details page; the route is
                      admin-only, so org managers keep the plain name. */}
                  {isAdmin ? (
                    <Link
                      to={`/users/${u.id}`}
                      className="font-medium hover:underline"
                    >
                      {u.username}
                    </Link>
                  ) : (
                    <span className="font-medium">{u.username}</span>
                  )}
                  <OrgRoleBadge role={u.org_role} showNone />
                  {u.role === "admin" && (
                    <Badge variant="warning" className="badge-sm">{t("operator.user_menu.system_role", { role: "admin" })}</Badge>
                  )}
                  {isAdmin && (
                    <Badge variant={u.tier === "commercial" ? "success" : "secondary"} className="badge-sm">{t("operator.users.tier", { tier: u.tier })}</Badge>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-destructive hover:text-destructive"
                    aria-label={t("operator.orgs.remove_user", { name: u.username })}
                    onClick={() => {
                      if (confirm(t("operator.orgs.remove_user_confirm", { name: u.username })))
                        removeUserM.mutate(u.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending phone invitations — people who have been invited but have not
          registered yet. Recorded even when the SMS channel is off, so without
          this section they were invisible. */}
      <PendingInvitations orgId={orgId} canManage={canManageOrg} isAdmin={isAdmin} />

      {/* Units */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-baseline gap-2">
            <h4 className="font-semibold text-sm">{t("operator.orgs.units_heading", { count: org.unit_ids.length })}</h4>
            {typeof org.total_capacity_kw === "number" && org.unit_ids.length > 0 && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-default">
                      {t("operator.orgs.capacity_kw", { power: org.total_capacity_kw.toLocaleString(FORMAT_LOCALE) })}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {typeof org.nominal_line_voltage === "number"
                      ? t("operator.orgs.capacity_hint_voltage", { amps: org.total_capacity_amps?.toLocaleString(FORMAT_LOCALE) ?? "", volts: String(org.nominal_line_voltage) })
                      : t("operator.orgs.capacity_hint", { amps: org.total_capacity_amps?.toLocaleString(FORMAT_LOCALE) ?? "" })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setAddUnitOpen(true)}>{t("operator.orgs.add_unit_button")}</Button>
          )}
        </div>
        {org.unit_ids.length === 0 ? (
          <div className="text-sm opacity-50 italic">{t("operator.orgs.no_units")}</div>
        ) : (
          <TooltipProvider delayDuration={150}>
          <div className="space-y-1">
            {((org.units && org.units.length > 0
              ? org.units
              : org.unit_ids.map((uid) => ({ unit_id: uid }))) as OrganizationUnitSummary[]
            ).map((u) => {
              const isPaid =
                u.work_mode === "commercial-public" && u.pricing_mode === "paid";
              const status = statusIconProps(t, u.is_online, u.state_name);
              const StatusIcon = status.Icon;
              // Show the unit's custom name (same rule as the dashboard table:
              // the backend default is `Unit <id>`, which is not a real name).
              const hasCustomName = !!u.name && u.name !== `Unit ${u.unit_id}`;
              return (
                <div key={u.unit_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center" aria-label={status.label}>
                          <StatusIcon className={`h-3.5 w-3.5 ${status.color}`} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{status.label}</TooltipContent>
                    </Tooltip>
                    <Link
                      to={`/unit/${encodeURIComponent(u.unit_id)}`}
                      state={{ backTo: "/organizations", backLabelKey: "operator.nav.organizations" }}
                      className="min-w-0 truncate text-primary hover:underline"
                    >
                      {hasCustomName ? (
                        <>
                          <span className="font-medium">{u.name}</span>
                          <span className="ml-2 font-mono text-xs opacity-60">{u.unit_id}</span>
                        </>
                      ) : (
                        <span className="font-mono">{u.unit_id}</span>
                      )}
                    </Link>
                    {isPaid && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center shrink-0" aria-label={t("operator.orgs.paid_charging")}>
                            <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t("operator.orgs.paid_charging_hint")}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-destructive hover:text-destructive"
                    aria-label={t("operator.orgs.remove_unit", { unitId: u.unit_id })}
                    onClick={() => {
                      if (confirm(t("operator.orgs.remove_unit_confirm", { unitId: u.unit_id })))
                        removeUnitM.mutate(u.unit_id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>
          </TooltipProvider>
        )}
      </div>

      {/* Reports — the full-CSV export stays admin-only (it always includes money);
          the in-UI report opens for a manager too, as a money-free session-only view
          (canSeeMoney={isAdmin}; the server also withholds the money for a manager). */}
      {isAdmin && mode === "panel" && <ExportSessionsSection orgId={orgId} />}
      {canManageOrg && mode === "page" && (
        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="font-semibold text-sm">{t("operator.orgs.reports_title")}</h4>
          <OrgSessionsReport orgId={orgId} units={org.units} canSeeMoney={isAdmin} />
        </div>
      )}

      {/* Edit Modal */}
      <OrgFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        title={t("operator.orgs.edit_title")}
        initial={{
          name: org.name,
          contact_person: org.contact_person || "",
          contact_phone: org.contact_phone || "",
          contact_email: org.contact_email || "",
          paid_until: org.paid_until || "",
          comment: org.comment || "",
        }}
        onSubmit={(data) => editM.mutate(data)}
        isPending={editM.isPending}
      />

      {/* Add User Modal */}
      <AddUserModal
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        orgId={orgId}
        existingUserIds={new Set(org.users.map((u) => u.id))}
        onAdded={refreshDetail}
      />

      {/* Add Unit Modal */}
      <AddUnitModal
        open={addUnitOpen}
        onOpenChange={setAddUnitOpen}
        orgId={orgId}
        existingUnitIds={new Set(org.unit_ids)}
        onAdded={refreshDetail}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export function OrganizationsPage() {
  // language/revision rebuild the memoized column headers after a catalog switch.
  const { t, language, revision } = useLocalization(localization);
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Client-side sorting for the org list (Users / Units / Paid Until, plus the
  // text columns). Default: unsorted (backend order by name).
  const [sorting, setSorting] = useState<SortingState>([]);

  const q = useQuery({
    queryKey: ["organizations"],
    queryFn: fetchOrganizations,
    refetchInterval: 20_000,
  });

  const filteredOrgs = useMemo(() => {
    const orgs = q.data?.organizations ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter((o) => {
      const haystack = [
        o.name,
        o.contact_person,
        o.contact_email,
        o.contact_phone,
        o.account_custom_id,
        o.comment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [q.data, searchTerm]);

  // Detail panel
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(() => {
    const saved = localStorage.getItem("orgsSelectedPanel");
    return saved ? Number(saved) : null;
  });

  const navigate = useNavigate();
  const handleOrgClick = useCallback((org: Organization) => {
    setSelectedOrgId(org.id);
    localStorage.setItem("orgsSelectedPanel", String(org.id));
  }, []);
  // Double-click opens the full details page (side panel is single click).
  const handleOrgOpen = useCallback(
    (org: Organization) => navigate(`/organizations/${org.id}`),
    [navigate],
  );

  const closePanel = useCallback(() => {
    setSelectedOrgId(null);
    localStorage.removeItem("orgsSelectedPanel");
  }, []);

  const createM = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      toast.success(t("operator.orgs.created"));
      setCreateOpen(false);
      q.refetch();
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.create_failed" })),
  });

  const hasAccountCustomId = useMemo(
    () => (q.data?.organizations ?? []).some((o) => !!o.account_custom_id),
    [q.data]
  );

  const columns = useMemo<ColumnDef<Organization, any>[]>(
    () => {
      const cols: ColumnDef<Organization, any>[] = [
        {
          accessorKey: "name",
          header: t("operator.orgs.column_name"),
          cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
        },
        {
          accessorKey: "contact_person",
          header: t("operator.orgs.column_contact"),
          cell: ({ getValue }) => getValue() || <span className="opacity-40">—</span>,
        },
      ];
      if (hasAccountCustomId) {
        cols.push({
          accessorKey: "account_custom_id",
          header: t("operator.orgs.column_account_id"),
          cell: ({ getValue }) => {
            const v = getValue() as string | null | undefined;
            return v ? <span className="font-mono text-xs">{v}</span> : <span className="opacity-40">—</span>;
          },
        });
      }
      cols.push(
        {
          accessorKey: "user_count",
          header: t("operator.orgs.column_users"),
          cell: ({ getValue }) => <Badge variant="info" className="badge-sm">{getValue()}</Badge>,
        },
        {
          accessorKey: "unit_count",
          header: t("operator.orgs.column_units"),
          // <online>/<total>. online_unit_count is live (present when a unit
          // reader is wired); without it we fall back to the plain total.
          cell: ({ row }) => {
            const total = row.original.unit_count;
            const online = row.original.online_unit_count;
            const live = typeof online === "number";
            return (
              <Badge
                variant="info"
                className="badge-sm tabular-nums"
                title={live ? t("operator.orgs.units_online_hint", { online: String(online), total: String(total) }) : undefined}
              >
                {live ? `${online}/${total}` : total}
              </Badge>
            );
          },
        },
        {
          // Live draw / total nameplate capacity (sum of each charger's max
          // current). Sorted by capacity; map to undefined when absent so
          // tanstack sorts empty rows last.
          id: "capacity_kw",
          accessorFn: (o) => o.total_capacity_kw ?? undefined,
          header: t("operator.orgs.column_capacity"),
          cell: ({ row }) => {
            const kw = row.original.total_capacity_kw;
            if (typeof kw !== "number") return <span className="opacity-40">—</span>;
            const cur = row.original.current_power_kw ?? 0;
            const amps = row.original.total_capacity_amps;
            const curAmps = row.original.current_power_amps;
            // Same digits as before (up to 3 decimals), in the fixed en-US format (P-3).
            const title =
              typeof amps === "number"
                ? t("operator.orgs.capacity_draw_hint", {
                    current: typeof curAmps === "number" ? curAmps.toLocaleString(FORMAT_LOCALE) : "0",
                    capacity: amps.toLocaleString(FORMAT_LOCALE),
                  })
                : undefined;
            return (
              <span className="tabular-nums" title={title}>
                {t("operator.orgs.capacity_cell", { current: cur.toLocaleString(FORMAT_LOCALE), capacity: kw.toLocaleString(FORMAT_LOCALE) })}
              </span>
            );
          },
        },
        {
          id: "paid_until",
          // Map null → undefined so tanstack's sortUndefined can push orgs with
          // no paid-until date to the end in BOTH sort directions (null is not
          // treated as undefined by the sorter otherwise).
          accessorFn: (o) => o.paid_until ?? undefined,
          header: t("operator.orgs.column_paid_until"),
          sortUndefined: "last",
          cell: ({ getValue }) => paidBadge((getValue() as string | undefined) ?? null),
        },
      );
      return cols;
    },
    [hasAccountCustomId, t, language, revision]
  );

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.nav.organizations")}</h1>
          <div className="text-sm opacity-70">{t("operator.orgs.subtitle")}</div>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>{t("operator.orgs.new_button")}</Button>
        )}
      </div>

      {/* Table + Detail Panel */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-1 min-w-0">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
              <CardTitle>{t("operator.orgs.all_title")}</CardTitle>
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                <Input
                  className="pl-9"
                  placeholder={t("operator.org_selector.search_placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {q.isError ? (
                <div className="text-sm text-error">{t("operator.orgs.load_failed")}</div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredOrgs}
                  enableSorting
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isLoading={q.isLoading}
                  getRowId={(o) => String(o.id)}
                  onRowClick={handleOrgClick}
                  onRowDoubleClick={handleOrgOpen}
                  selectedRowId={selectedOrgId ? String(selectedOrgId) : undefined}
                  emptyMessage={t(searchTerm ? "operator.orgs.empty_search" : "operator.orgs.empty")}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <SlidePanel open={!!selectedOrgId} onClose={closePanel} title={t("operator.orgs.details_title")} width="w-[520px]">
          {selectedOrgId && (
            <OrgDetailPanel orgId={selectedOrgId} onDeleted={closePanel} />
          )}
        </SlidePanel>
      </div>

      {/* Create Modal */}
      <OrgFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t("operator.orgs.new_title")}
        initial={emptyForm}
        onSubmit={(data) => createM.mutate(data)}
        isPending={createM.isPending}
      />
    </div>
  );
}
